'use client';

import { FC, useEffect, useRef, useState } from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import useDeviceMedia from '@/hooks/useDeviceMedia';
import SearchContext from '@/context/SearchResultsContext';
import CategoriesFrontend from '@/lib/Algonomy/services/CategoriesFrontend';
import { createQueryString, createUrl, getQueryParams } from '@/utils/routerUtils';
import { SearchParams, QueryValues } from '@/types/algonomy';
import { CategoryContextContainerProps } from './types';
import useCheckoutNeedle from '@/hooks/useCheckoutNeedle';
import { cookieStorage } from '@/services/cookie';

const categoryAlgonomyFrontend = new CategoriesFrontend(cookieStorage);

const CategoryContextContainer: FC<CategoryContextContainerProps> = ({ children, isMobile }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isScrollOnLoad = useRef<boolean>(false);
  const mobile = useDeviceMedia('(max-width:1279px)', isMobile);
  const viewOption = searchParams?.get('viewOption') || '';
  const sort = searchParams?.get('sort') || '';
  useCheckoutNeedle(isLoading);

  useEffect(() => {
    if (isLoading) {
      setIsLoading(false);
    }
    if (window?.scrollY > 0 && !mobile && isScrollOnLoad.current) {
      const searchContainer = document.getElementById('search-content');
      searchContainer?.scrollIntoView({ behavior: 'smooth' });
      isScrollOnLoad.current = false;
    }
  }, [pathname, searchParams]);

  useEffect(() => {
    if (document) {
      const htmlRoot = document?.querySelector('html');
      if (openFilter) {
        htmlRoot?.classList.add('preventScroll');
      } else {
        htmlRoot?.classList.remove('preventScroll');
      }
    }
    return () => {
      const htmlRoot = document?.querySelector('html');
      htmlRoot?.classList.remove('preventScroll');
    };
  }, [openFilter]);

  const redirectWithFilters = (filterQuery: string, val: boolean, filterValue: string) => {
    const rest = categoryAlgonomyFrontend.getFilterValues(getQueryParams(searchParams as URLSearchParams));
    const filterQueries: QueryValues = categoryAlgonomyFrontend.redirectToNextSearch(filterQuery, val, rest);
    const searchQuery = categoryAlgonomyFrontend.validateSearchQueryParams({
      viewOption,
      sort,
      ...filterQueries,
    });
    const params = createQueryString({ ...searchQuery });
    const url = createUrl(pathname as string, params);
    router.push(url, { scroll: false });
  };

  const handleSelectFilter = (facet: string, filterValue: string, filterName: string, val: boolean) => {
    if (!isLoading) {
      setIsLoading(true);
      isScrollOnLoad.current = true;
      redirectWithFilters(filterName, val, filterValue);
    }
  };

  const handleChangeTab = (productType: string) => {
    if (!isLoading) {
      return;
    }
    return;
  };

  const handleSort = (sortValue: string) => {
    if (!isLoading) {
      setIsLoading(true);
      isScrollOnLoad.current = true;
      const filterQueries: SearchParams = categoryAlgonomyFrontend.createURLQuery(
        '',
        getQueryParams(searchParams as URLSearchParams),
        '',
      ) as SearchParams;
      const searchQuery = categoryAlgonomyFrontend.validateSearchQueryParams({
        viewOption,
        ...filterQueries,
      });
      const params = createQueryString({ sort: sortValue, ...searchQuery });
      const url = createUrl(pathname as string, params);
      router.push(url, { scroll: false });
    }
  };

  const handleOnClickOutside = () => {
    if (openFilter && mobile) {
      // need to use timeout this in order to prevent card being cliked on mobile
      // stopPropagation doesn't work
      setTimeout(() => {
        setOpenFilter(false);
      }, 150);
    }
  };

  const handleApplyFilters = () => {
    if (!isLoading) {
      setOpenFilter(false);
    }
  };

  return (
    <SearchContext.Provider
      value={{
        isLoading,
        openFilter,
        mobile,
        setIsLoading,
        handleSelectFilter,
        handleChangeTab,
        setOpenFilter,
        handleSort,
        handleOnClickOutside,
        handleApplyFilters,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export default CategoryContextContainer;
